import React from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { LoaderComponent } from 'shared/LoaderComponent';
import { useListAccountsQuery } from 'hooks/queries/useListAccountsQuery';
import { dollarFormat } from 'utils/utils';
import { Button } from 'primereact/button';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';

/**
 * TrustAccountingFiltersV2 Component
 * Filter component for Trust Accounting V2 with date range and account selection
 *
 * @param {Object} props
 * @param {Date|Array} props.dateFilter - Current date filter value
 * @param {Function} props.onChangeCalendar - Handler for date filter changes
 * @param {string|null} props.selectedAccount - Selected account ID
 * @param {Function} props.onChangeAccount - Handler for account changes
 * @param {Function} props.refreshData - Handler for resetting filters
 * @param {boolean} props.caseView - Whether in case-specific view
 * @param {number} props.totalTrustBalance - Total trust balance
 * @param {number} props.designatedAsTempTrustFunds - Designated temporary trust funds
 * @param {number} props.availableBalance - Available balance to bill against
 * @param {number} props.openingBalance - Opening balance
 * @param {number} props.reconciledBalance - Reconciled balance
 */
const TrustAccountingFiltersV2 = ({
  dateFilter,
  onChangeCalendar,
  selectedAccount,
  onChangeAccount,
  refreshData,
  caseView,
  totalTrustBalance = 0,
  designatedAsTempTrustFunds = 0,
  availableBalance = 0,
  openingBalance = 0,
  reconciledBalance = 0,
}) => {
  // Fetch trust accounts
  const { accountList, isAccountLoading } = useListAccountsQuery({
    accountType: 'trust',
    excludeFromLoading: true,
  });
  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-lg-0 p-2 w-100 align-items-center justify-content-between">
      <div className="d-flex flex-wrap align-items-center p-2 filter-wraper">
        {(selectedAccount || caseView) && (
          <label className="p-0 m-0 filter-label">
            Total Trust Balance: <span className="text-bold black-600">{dollarFormat(totalTrustBalance)}</span> of which{' '}
            <span className="text-bold black-600">{dollarFormat(designatedAsTempTrustFunds)}</span> is designated as temporary trust funds,
            leaving <span className="text-bold black-600">{dollarFormat(availableBalance)}</span> as Trust Funds Available to Bill Against.
          </label>
        )}
        {selectedAccount && !caseView && openingBalance !== 0 && (
          <label className="p-0 m-0 filter-label mr-2">
            Opening Balance:{' '}
            <span className="text-bold black-600" style={{ marginRight: '10px' }}>
              {dollarFormat(openingBalance)}
            </span>
          </label>
        )}
        {selectedAccount && !caseView && reconciledBalance !== 0 && (
          <label className="p-0 m-0 filter-label">
            Reconciled Balance:{' '}
            <span className="text-bold black-600" style={{ marginRight: '10px' }}>
              {dollarFormat(reconciledBalance)}
            </span>
          </label>
        )}
      </div>
      <div className="d-block d-lg-none w-100">
        <Button
          className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-controls="trust-filter-section"
          label="Filters"
        >
          <span
            style={{
              fontSize: 18,
              transition: 'transform 0.2s',
              display: 'inline-block',
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <i className="fa fa-solid fa-angle-down"></i>
          </span>
        </Button>
      </div>
      <div
        className={`d-flex flex-wrap align-items-center gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
        id="trust-filter-section"
        aria-hidden={!showFilters}
      >
        <label className="text-bold black-600 p-0 m-0 filter-label">Filter:</label>
        <div className="d-flex flex-column filter-wraper">
          <Calendar
            className="input-shadow filter-calender"
            id="icon"
            selectionMode="range"
            value={dateFilter}
            onChange={onChangeCalendar}
            showIcon
            placeholder="Date"
          />
        </div>
        {!caseView && (
          <div className="d-flex flex-column filter-wraper">
            <Dropdown
              placeholder="By Account"
              options={accountList}
              value={selectedAccount}
              onChange={onChangeAccount}
              optionLabel="account_name"
              optionValue="account_id"
              className="w-100"
              filter
              showClear
              panelFooterTemplate={isAccountLoading ? LoaderComponent : null}
            />
          </div>
        )}
        <div className="d-flex flex-column mx-2">
          <div className="pointer ms-0">
            <i className="fas fa-sync-alt" onClick={refreshData}></i>
            <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustAccountingFiltersV2;
